import React, { useEffect, useState } from "react";
import { executeQuery } from "../../../graphql/client";

// Captain-only dashboard strip (#309 build item 3). Three lines a
// captain can act on before their night: who is coming to the next
// event they host (and which of them are first-timers to go find),
// and how their referral link is doing this month. Backed by the
// read-only `myCaptainBrief` query; the server gates it to
// captain-or-above, and the dashboard only mounts this for the
// Captains / Admins groups so members never fire a failing call.

const BRIEF_QUERY = `
  query MyCaptainBrief {
    myCaptainBrief
  }
`;

const formatIsoDate = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso || "";
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many || one + "s"}`;

const lineStyle = {
  display: "flex",
  gap: 8,
  alignItems: "flex-start",
  fontSize: 13.5,
  color: "#2b3445",
  lineHeight: 1.45,
};

const Line = ({ icon, label, children }) => (
  <div style={lineStyle}>
    <span aria-hidden="true" style={{ flex: "0 0 auto" }}>
      {icon}
    </span>
    <span style={{ minWidth: 0 }}>
      <span style={{ fontWeight: 700, color: "#1c3a5e" }}>{label} </span>
      {children}
    </span>
  </div>
);

export const nightLine = (brief) => {
  const n = brief && brief.nextNight;
  if (!n) {
    return brief && brief.hostTermsKnown
      ? "No upcoming event lists you as host yet. Add yourself as a host when you create one and it shows up here."
      : "We couldn't match your name to any event hosts yet — ask David to link your captain profile.";
  }
  const when = [formatIsoDate(n.isoDate), n.time].filter(Boolean).join(" · ");
  const who =
    n.going === 0 && n.maybe === 0
      ? "no RSVPs yet"
      : `${plural(n.going, "going")}${n.maybe ? `, ${n.maybe} maybe` : ""}`;
  const more =
    brief.upcomingHostedCount > 1
      ? ` (+${brief.upcomingHostedCount - 1} more of yours coming up)`
      : "";
  return `${n.title || "Your event"} — ${when} · ${who}${more}`;
};

export const firstTimerLine = (brief) => {
  const n = brief && brief.nextNight;
  if (!n) return null;
  if (!n.historyKnown) return "Couldn't check RSVP history right now.";
  if (n.going === 0) return "Nobody has RSVP'd yet — share the event.";
  if (n.firstTimerCount === 0) return "Everyone going has been out with us before.";
  const names = n.firstTimerNames || [];
  const unnamed = n.firstTimerCount - names.length;
  const list = names.join(", ") + (unnamed > 0 ? ` + ${unnamed} more` : "");
  return `${plural(n.firstTimerCount, "first-timer")} expected — find them early: ${list}`;
};

export const referralLine = (brief) => {
  const r = brief && brief.referrals;
  if (!r) return "Referral count unavailable right now.";
  const m = r.thisMonth || { started: 0, accounts: 0, paid: 0 };
  const a = r.allTime || { started: 0, accounts: 0, paid: 0 };
  const rank =
    r.rankThisMonth && r.captainsRanked > 1
      ? ` · #${r.rankThisMonth} of ${r.captainsRanked} captains`
      : "";
  if (m.started === 0 && a.started === 0) {
    return `Nobody has joined through your link yet${rank}. Share it above — it's how your count moves.`;
  }
  const paid = m.paid ? `, ${m.paid} paying` : "";
  return `${plural(m.started, "person", "people")} through your link in ${r.monthLabel}${paid} · ${a.started} all time${rank}`;
};

const CaptainBrief = ({ show }) => {
  const [brief, setBrief] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | hidden

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await executeQuery(BRIEF_QUERY, {});
        if (cancelled) return;
        const parsed = JSON.parse(res?.myCaptainBrief || "null");
        if (!parsed) {
          setState("hidden");
          return;
        }
        setBrief(parsed);
        setState("ready");
      } catch (e) {
        // Not a captain server-side, or the API is down — the strip
        // is a convenience, never an error on the dashboard.
        if (!cancelled) setState("hidden");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show]);

  if (!show || state !== "ready") return null;

  const ft = firstTimerLine(brief);

  return (
    <div
      style={{
        background: "#f3f7fc",
        border: "1px solid #c5d6ea",
        borderRadius: 10,
        padding: "14px 16px",
        marginBottom: 16,
        display: "grid",
        gap: 6,
      }}
      aria-label="Your captain brief"
    >
      <div style={{ fontWeight: 700, color: "#1c3a5e", marginBottom: 2 }}>
        <span role="img" aria-label="captain">
          ⚓
        </span>{" "}
        Your captain brief
        {brief.captainName ? (
          <span style={{ fontWeight: 500, color: "#4a5a70" }}>
            {" "}
            · {brief.captainName}
          </span>
        ) : null}
      </div>
      <Line icon="🗓️" label="Your next night:">
        {nightLine(brief)}
      </Line>
      {ft && (
        <Line icon="🆕" label="First-timers:">
          {ft}
        </Line>
      )}
      <Line icon="🤝" label="Brought by you:">
        {referralLine(brief)}
      </Line>
    </div>
  );
};

export default CaptainBrief;
